/**
 * The chat API, mounted separately from the admin API and answering to a different rule: any
 * authenticated member of the organisation, not only an administrator.
 *
 * Everything JSON-shaped goes through `apiRequest` in `client.ts`, so token handling and 401
 * behaviour are shared. The one exception is `streamAnswer`, which reads its response body
 * incrementally and therefore holds its own `fetch` — `client.ts`'s `request` awaits the whole body,
 * which is exactly what streaming must not do.
 */

import { apiRequest, ApiError, CHAT_BASE_URL, getAccessToken } from "./client";
import type {
  ChatFeedbackRequest,
  ChatMessage,
  ChatSettings,
  DomainResolution,
  SearchResponse,
} from "./types";

/** One event from the answer stream. `type` discriminates; see `backend/chat_stream.py`. */
export type ChatEvent =
  | { type: "delta"; text: string }
  | { type: "tool"; name: string; arguments: Record<string, unknown> }
  | { type: "search"; payload: SearchResponse }
  | { type: "domains"; payload: DomainResolution }
  | { type: "suggestions"; items: string[] }
  | { type: "done"; reply: string; hit_turn_limit: boolean }
  | { type: "error"; message: string };

const SSE_DATA_PREFIX = "data: ";
const FRAME_SEPARATOR = "\n\n";

const UNREACHABLE = "Could not reach the server. Check your connection and try again.";

/** GET /api/examples — the starter questions shown on an empty conversation. */
export function fetchExamples(): Promise<string[]> {
  return apiRequest<string[]>(CHAT_BASE_URL, "/api/examples");
}

/** GET /api/settings — how many cards to draw before offering the rest on their own page. */
export function fetchChatSettings(): Promise<ChatSettings> {
  return apiRequest<ChatSettings>(CHAT_BASE_URL, "/api/settings");
}

/** GET /api/history — this user's conversation so far, so a reload resumes it. */
export function fetchChatHistory(): Promise<ChatMessage[]> {
  return apiRequest<ChatMessage[]>(CHAT_BASE_URL, "/api/history");
}

/** DELETE /api/history — forget this user's conversation. */
export function clearChatHistory(): Promise<void> {
  return apiRequest<void>(CHAT_BASE_URL, "/api/history", { method: "DELETE" });
}

/** POST /api/feedback — record a rating through the surface's own feedback tool. */
export function sendChatFeedback(body: ChatFeedbackRequest): Promise<void> {
  return apiRequest<void>(CHAT_BASE_URL, "/api/feedback", { method: "POST", body });
}

/**
 * POST /api/message, delivering each event as it arrives.
 *
 * @param message What the user asked.
 * @param onEvent Called once per event, in order. Always ends with `done` or `error`.
 * @param signal Aborts the request; the caller uses it to cancel an in-flight answer.
 * @throws ApiError if the request is refused before streaming begins — 401 (signed out), 403 (the
 *   break-glass account, which chat cannot serve) or a network failure. A failure *after* streaming
 *   starts cannot change the status code, so it arrives as an `error` event instead.
 */
export async function streamAnswer(
  message: string,
  onEvent: (event: ChatEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await openStream(message, signal);
  const reader = response.body?.getReader();
  if (!reader) {
    throw new ApiError(0, UNREACHABLE);
  }
  const decoder = new TextDecoder();
  let buffered = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffered += decoder.decode(value, { stream: true });
    buffered = drainFrames(buffered, onEvent);
  }
}

/** Start the request and check the status, before any body has been read. */
async function openStream(message: string, signal?: AbortSignal): Promise<Response> {
  const token = await getAccessToken();
  let response: Response;
  try {
    response = await fetch(`${CHAT_BASE_URL}/api/message`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ message }),
      signal,
    });
  } catch (error) {
    // An abort is the caller's own doing and must stay distinguishable from a failure.
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new ApiError(0, UNREACHABLE);
  }
  if (!response.ok) {
    throw new ApiError(response.status, await detailOf(response));
  }
  return response;
}

/** Emit every complete frame in the buffer, returning the incomplete remainder. */
function drainFrames(buffered: string, onEvent: (event: ChatEvent) => void): string {
  const frames = buffered.split(FRAME_SEPARATOR);
  // The last piece is whatever arrived after the final separator: possibly a partial frame, so it
  // stays buffered until the next chunk completes it.
  const remainder = frames.pop() ?? "";
  for (const frame of frames) {
    const event = parseFrame(frame);
    if (event) {
      onEvent(event);
    }
  }
  return remainder;
}

/** One `data:` frame as an event, or null for a keep-alive or an unparseable line. */
function parseFrame(frame: string): ChatEvent | null {
  const line = frame.trim();
  if (!line.startsWith(SSE_DATA_PREFIX)) {
    return null;
  }
  try {
    return JSON.parse(line.slice(SSE_DATA_PREFIX.length)) as ChatEvent;
  } catch {
    // A frame we cannot read is one event lost, not a reason to abandon the answer in progress.
    return null;
  }
}

/** The `detail` from a refused request, for the message the user sees. */
async function detailOf(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    return typeof body.detail === "string" ? body.detail : "";
  } catch {
    return "";
  }
}
